import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  Typography,
} from "@mui/material";
import { getResponsiveDialogProps } from "../../theme/responsive";

const CloneSiteDialog = ({ open, onClose, site, onClone }) => {
  const [targetEnvironment, setTargetEnvironment] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setTargetEnvironment("");
    setError("");
    onClose();
  };

  const handleClone = async () => {
    if (!targetEnvironment) {
      setError("Please select a target environment");
      return;
    }

    if (targetEnvironment === site?.environment) {
      setError(
        "Target environment must be different from the current environment",
      );
      return;
    }

    setLoading(true);
    setError("");

    try {
      await onClone({ targetEnvironment });
      handleClose();
    } catch (err) {
      setError(err.message || "Failed to clone site");
    } finally {
      setLoading(false);
    }
  };

  const environments = ["SANDBOX", "STAGING", "PRODUCTION"];
  const availableEnvironments = environments.filter(
    (env) => env !== site?.environment,
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      {...getResponsiveDialogProps("medium")}
    >
      <DialogTitle>Clone Site</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Clone <strong>{site?.name}</strong> from{" "}
            <strong>{site?.environment}</strong> to a different environment.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Target Environment</InputLabel>
            <Select
              value={targetEnvironment}
              onChange={(e) => setTargetEnvironment(e.target.value)}
              label="Target Environment"
              disabled={loading}
            >
              {availableEnvironments.map((env) => (
                <MenuItem key={env} value={env}>
                  {env}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Alert severity="info" sx={{ mt: 2 }}>
            This will create a complete copy of your site including files,
            database, and configurations in the selected environment.
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleClone}
          disabled={loading || !targetEnvironment}
        >
          {loading ? "Cloning..." : "Clone Site"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CloneSiteDialog;
